/**
 * Avatar Component
 * 
 * Displays user profile image or initials fallback
 * Includes camera icon overlay for image upload
 */

import React, { useRef, useState } from 'react';
import { Camera, X } from 'lucide-react';
import type { Profile } from '../../types/profile.types';

interface AvatarProps {
  profile?: Profile;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showUpload?: boolean;
  onUpload?: (file: File) => void;
  onRemove?: () => void;
  className?: string;
}

/**
 * Generate initials from name
 */
function getInitials(profile?: Profile): string {
  if (!profile) return '?';
  
  const firstName = profile.firstName || '';
  const lastName = profile.lastName || '';
  const email = profile.email || '';
  
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  
  if (firstName) {
    return firstName[0].toUpperCase();
  }
  
  if (email) {
    return email[0].toUpperCase();
  }
  
  return '?';
}

/**
 * Generate color from user ID
 */
function getColorFromUserId(userId: string): string {
  // Simple hash function to generate consistent color
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate a color in the blue-green range
  const hue = Math.abs(hash % 180) + 180; // 180-360 range (blue-green)
  return `hsl(${hue}, 70%, 50%)`;
}

const sizeClasses = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-12 w-12 text-sm',
  lg: 'h-16 w-16 text-base',
  xl: 'h-24 w-24 text-xl'
};

const deleteButtonSizes = {
  sm: 'h-5 w-5',
  md: 'h-6 w-6',
  lg: 'h-7 w-7',
  xl: 'h-8 w-8'
};

const deleteIconSizes = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
  xl: 'h-4 w-4'
};

export function Avatar({
  profile,
  size = 'md',
  showUpload = false,
  onUpload,
  onRemove,
  className = ''
}: AvatarProps): React.ReactElement {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [imageError, setImageError] = useState(false);

  const initials = getInitials(profile);
  const backgroundColor = profile?.userId ? getColorFromUserId(profile.userId) : '#6B7280';
  const sizeClass = sizeClasses[size];

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onUpload) {
      onUpload(file);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    if (showUpload && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove();
    }
  };

  return (
    <div
      className={`relative inline-flex items-center justify-center rounded-full ${sizeClass} ${className}`}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      style={{
        backgroundColor: profile?.profileImageUrl && !imageError ? 'transparent' : backgroundColor,
      }}
      role="img"
      aria-label={profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || profile.email : 'User avatar'}
    >
      {/* Profile Image */}
      {profile?.profileImageUrl && !imageError ? (
        <img
          src={profile.profileImageUrl}
          alt={profile.firstName || profile.email || 'Profile'}
          className="h-full w-full rounded-full object-cover"
          onError={() => setImageError(true)}
        />
      ) : (
        <span
          className="font-semibold text-white"
          style={{ color: '#FFFFFF' }}
        >
          {initials}
        </span>
      )}

      {/* Camera Icon Overlay (bottom right) - Clickable */}
      {showUpload && (
        <button
          onClick={handleClick}
          className="absolute bottom-0 right-0 flex items-center justify-center rounded-full bg-primary-600 hover:bg-primary-700 text-white p-1.5 shadow-lg border-2 border-white dark:border-gray-800 transition-colors cursor-pointer z-10"
          aria-label="Change profile image"
          type="button"
        >
          <Camera className="h-4 w-4" aria-hidden="true" />
        </button>
      )}

      {/* Remove Button (if image exists) - shown on hover */}
      {profile?.profileImageUrl && !imageError && onRemove && isHovering && (
        <button
          onClick={handleRemove}
          className={`absolute top-0 right-0 ${deleteButtonSizes[size]} rounded-full bg-red-500 hover:bg-red-600 active:bg-red-700 text-white shadow-lg border-2 border-white dark:border-gray-800 transition-all duration-200 flex items-center justify-center z-20 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2`}
          aria-label="Remove profile image"
          type="button"
        >
          <X className={deleteIconSizes[size]} aria-hidden="true" strokeWidth={3} />
        </button>
      )}

      {/* Hidden File Input */}
      {showUpload && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
          aria-label="Upload profile image"
        />
      )}
    </div>
  );
}

